import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, CheckCircle2, Download, Edit, FileCheck, Mail, MinusCircle, Send, Trash2, Undo2, XCircle } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Toolbar } from '@/components/Toolbar'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { FormField } from '@/components/FormField'
import { Checkbox } from '@/components/ui/checkbox'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import {
  useInvoice, usePostInvoice, useSendInvoice, useVoidInvoice, useDeleteDraftInvoice, useMarkInvoicePaid, useRemindInvoice,
  useIssueCreditNote, useIssueRefund, useWriteOffInvoice,
} from '@/features/invoicing/queries'
import { downloadInvoicePdf, formatInvoiceMoney } from '@/features/invoicing/format'
import { formatDate, formatDateTime } from '@/lib/locale'
import type { InvoiceStatus } from '@/features/invoicing/types'
import { isPendingApproval } from '@/features/invoicing/types'

const STATUS_VARIANT: Record<InvoiceStatus, 'default' | 'secondary' | 'danger' | 'outline' | 'success' | 'warning' | 'info'> = {
  draft: 'secondary', sent: 'default', viewed: 'default', partially_paid: 'default',
  paid: 'success', overdue: 'danger', void: 'secondary', canceled: 'secondary', written_off: 'secondary',
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const invoiceId = parseInt(id as string, 10)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission } = useAuth()

  const { data, isLoading } = useInvoice(invoiceId)
  const postMut = usePostInvoice()
  const sendMut = useSendInvoice()
  const voidMut = useVoidInvoice()
  const deleteDraftMut = useDeleteDraftInvoice()
  const markPaidMut = useMarkInvoicePaid()
  const remindMut = useRemindInvoice()
  const creditMut = useIssueCreditNote()
  const refundMut = useIssueRefund()
  const writeOffMut = useWriteOffInvoice()

  const [voidOpen, setVoidOpen] = useState(false)
  const [voidReason, setVoidReason] = useState('')
  const [deleteDraftOpen, setDeleteDraftOpen] = useState(false)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)
  const [markPaidDate, setMarkPaidDate] = useState(new Date().toISOString().slice(0, 10))
  const [markPaidMethod, setMarkPaidMethod] = useState('manual')
  const [markPaidAmount, setMarkPaidAmount] = useState('')
  const [creditOpen, setCreditOpen] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')
  const [refundOpen, setRefundOpen] = useState(false)
  const [refundPaymentId, setRefundPaymentId] = useState<string>('')
  const [refundAmount, setRefundAmount] = useState('')
  const [refundMethod, setRefundMethod] = useState('manual')
  const [refundReason, setRefundReason] = useState('')
  const [refundPostToAccounts, setRefundPostToAccounts] = useState(true)
  const [writeOffOpen, setWriteOffOpen] = useState(false)
  const [writeOffAmount, setWriteOffAmount] = useState('')
  const [writeOffReason, setWriteOffReason] = useState('')

  if (isLoading) {
    return <Page title="Invoice"><div className="flex justify-center py-20"><LoadingSpinner /></div></Page>
  }
  const inv = data?.invoice
  if (!inv) {
    return <Page title="Invoice not found"><Card><div className="p-8 text-center text-muted-foreground">Invoice not found.</div></Card></Page>
  }

  const canEdit = hasPermission('invoicing.invoices.create') && inv.status === 'draft'
  const canPost = hasPermission('invoicing.invoices.post') && inv.status === 'draft' && !inv.posted_at
  const canSend = hasPermission('invoicing.invoices.send') && !['void', 'canceled', 'paid', 'written_off'].includes(inv.status)
  // Delete-vs-void split: drafts that haven't been posted get a hard delete;
  // anything that's been posted/sent needs a void (reverses GL + keeps audit).
  const isDraftDeletable = inv.status === 'draft' && !inv.posted_at
  const canDeleteDraft = isDraftDeletable && hasPermission('invoicing.invoices.void')
  const canVoid = !isDraftDeletable && hasPermission('invoicing.invoices.void') && !['paid', 'void', 'canceled', 'written_off'].includes(inv.status)
  const canRecord = hasPermission('invoicing.payments.record') && !['paid', 'void', 'canceled', 'written_off'].includes(inv.status)
  const canRemind = hasPermission('invoicing.reminders.manage')
    && ['sent', 'viewed', 'partially_paid', 'overdue'].includes(inv.status)
    && !!inv.customer_email
  const canCredit = hasPermission('invoicing.credits.create')
    && !!inv.posted_at
    && !['void', 'canceled', 'written_off'].includes(inv.status)
    && +(Number(inv.total) - Number(inv.amount_paid || 0)).toFixed(2) > 0.005
  const canRefund = hasPermission('invoicing.refunds.create')
    && Number(inv.amount_paid || 0) > 0
    && inv.payments.length > 0
    && !['void', 'canceled'].includes(inv.status)
  const canWriteOff = hasPermission('invoicing.write_offs.create')
    && !!inv.posted_at
    && !['paid', 'void', 'canceled', 'written_off'].includes(inv.status)
    && +(Number(inv.total) - Number(inv.amount_paid || 0)).toFixed(2) > 0.005

  // Capture narrowed invoice in a variable TS can track across the handler closures below.
  const invoice = inv
  const outstandingNum = +(Number(invoice.total) - Number(invoice.amount_paid || 0)).toFixed(2)
  function openMarkPaid() {
    setMarkPaidAmount(outstandingNum > 0 ? outstandingNum.toFixed(2) : '')
    setMarkPaidDate(new Date().toISOString().slice(0, 10))
    setMarkPaidMethod('manual')
    setMarkPaidOpen(true)
  }

  async function onPost() {
    try {
      const res = await postMut.mutateAsync(invoice.id)
      if (isPendingApproval(res)) {
        toast({ title: 'Approval required', description: res.message })
        navigate(`/approvals/instances/${res.approval_instance_id}`)
        return
      }
      toast({ title: 'Posted to general ledger' })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Post failed', variant: 'destructive' })
    }
  }
  async function onSend() {
    try { await sendMut.mutateAsync(invoice.id); toast({ title: `Invoice emailed to ${invoice.customer_email}` }) }
    catch (e) { toast({ title: e instanceof Error ? e.message : 'Send failed', variant: 'destructive' }) }
  }
  async function onVoidConfirm() {
    if (!voidReason.trim()) { toast({ title: 'Reason is required', variant: 'destructive' }); return }
    try {
      await voidMut.mutateAsync({ id: invoice.id, reason: voidReason.trim() })
      toast({ title: 'Invoice voided' })
      setVoidOpen(false)
    } catch (e) { toast({ title: e instanceof Error ? e.message : 'Void failed', variant: 'destructive' }) }
  }
  async function onDeleteDraftConfirm() {
    try {
      await deleteDraftMut.mutateAsync(invoice.id)
      toast({ title: 'Draft deleted' })
      navigate('/invoicing/invoices')
    } catch (e) { toast({ title: e instanceof Error ? e.message : 'Delete failed', variant: 'destructive' }) }
  }
  async function onMarkPaidConfirm() {
    const amt = Number(markPaidAmount)
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: 'Enter a positive amount', variant: 'destructive' })
      return
    }
    if (amt - outstandingNum > 0.005) {
      toast({ title: `Amount exceeds outstanding balance (${outstandingNum.toFixed(2)} ${invoice.currency})`, variant: 'destructive' })
      return
    }
    try {
      const res = await markPaidMut.mutateAsync({
        id: invoice.id, amount: amt, received_at: markPaidDate, method: markPaidMethod,
      })
      toast({
        title: res.finalStatus === 'partially_paid'
          ? `Partial payment recorded (${(outstandingNum - amt).toFixed(2)} ${invoice.currency} outstanding)`
          : 'Marked as paid',
      })
      setMarkPaidOpen(false)
    } catch (e) { toast({ title: e instanceof Error ? e.message : 'Failed to record payment', variant: 'destructive' }) }
  }
  async function onDownload() {
    try { await downloadInvoicePdf(invoice.id, invoice.invoice_number) }
    catch (e) { toast({ title: e instanceof Error ? e.message : 'Download failed', variant: 'destructive' }) }
  }
  async function onRemind() {
    try {
      const r = await remindMut.mutateAsync(invoice.id)
      toast({ title: `Reminder #${r.reminder_number} sent to ${r.to}` })
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Reminder failed', variant: 'destructive' })
    }
  }
  function openCredit() {
    setCreditAmount(outstandingNum > 0 ? outstandingNum.toFixed(2) : '')
    setCreditReason('')
    setCreditOpen(true)
  }
  async function onCreditConfirm() {
    const amt = Number(creditAmount)
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: 'Enter a positive amount', variant: 'destructive' }); return
    }
    try {
      const res = await creditMut.mutateAsync({ invoice_id: invoice.id, amount: amt, reason: creditReason.trim() || undefined })
      if (isPendingApproval(res)) {
        toast({ title: 'Approval required', description: res.message })
        setCreditOpen(false)
        navigate(`/approvals/instances/${res.approval_instance_id}`)
        return
      }
      toast({ title: 'Credit note issued' })
      setCreditOpen(false)
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Credit note failed', variant: 'destructive' })
    }
  }
  function openRefund() {
    const firstPaid = invoice.payments[0]
    setRefundPaymentId(firstPaid ? String(firstPaid.id) : '')
    setRefundAmount(firstPaid ? Number(firstPaid.amount_applied).toFixed(2) : '')
    setRefundMethod(firstPaid?.method || 'manual')
    setRefundReason('')
    setRefundPostToAccounts(true)
    setRefundOpen(true)
  }
  async function onRefundConfirm() {
    const pid = parseInt(refundPaymentId, 10)
    const amt = Number(refundAmount)
    if (!Number.isFinite(pid) || pid <= 0) {
      toast({ title: 'Select which payment to refund', variant: 'destructive' }); return
    }
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: 'Enter a positive amount', variant: 'destructive' }); return
    }
    try {
      const res = await refundMut.mutateAsync({
        invoice_id: invoice.id,
        customer_payment_id: pid,
        amount: amt,
        method: refundMethod,
        reason: refundReason.trim() || undefined,
        postToAccounts: refundPostToAccounts,
      })
      if (isPendingApproval(res)) {
        toast({ title: 'Approval required', description: res.message })
        setRefundOpen(false)
        navigate(`/approvals/instances/${res.approval_instance_id}`)
        return
      }
      toast({ title: 'Refund recorded' })
      setRefundOpen(false)
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Refund failed', variant: 'destructive' })
    }
  }
  function openWriteOff() {
    setWriteOffAmount(outstandingNum > 0 ? outstandingNum.toFixed(2) : '')
    setWriteOffReason('')
    setWriteOffOpen(true)
  }
  async function onWriteOffConfirm() {
    const amt = Number(writeOffAmount)
    if (!Number.isFinite(amt) || amt <= 0) {
      toast({ title: 'Enter a positive amount', variant: 'destructive' }); return
    }
    if (!writeOffReason.trim()) {
      toast({ title: 'Reason is required', variant: 'destructive' }); return
    }
    try {
      const res = await writeOffMut.mutateAsync({
        invoice_id: invoice.id,
        amount: amt,
        reason: writeOffReason.trim(),
      })
      if (isPendingApproval(res)) {
        toast({ title: 'Approval required', description: res.message })
        setWriteOffOpen(false)
        navigate(`/approvals/instances/${res.approval_instance_id}`)
        return
      }
      toast({ title: 'Invoice written off' })
      setWriteOffOpen(false)
    } catch (e) {
      toast({ title: e instanceof Error ? e.message : 'Write-off failed', variant: 'destructive' })
    }
  }

  return (
    <Page
      title={inv.invoice_number}
      description={`${inv.customer_display_name} · ${formatInvoiceMoney(inv.total, inv.currency)}`}
      actions={
        <Toolbar align="end">
          <Button variant="ghost" asChild><Link to="/invoicing/invoices"><ArrowLeft className="mr-1 size-4" /> Back</Link></Button>
          {canEdit && <Button variant="secondary" onClick={() => navigate(`/invoicing/invoices/${inv.id}/edit`)}><Edit className="mr-1 size-4" /> Edit</Button>}
          <Button variant="secondary" onClick={onDownload}><Download className="mr-1 size-4" /> Download PDF</Button>
          {canPost && <Button variant="secondary" onClick={onPost} disabled={postMut.isPending}><FileCheck className="mr-1 size-4" /> Post to GL</Button>}
          {canSend && <Button onClick={onSend} disabled={sendMut.isPending}><Mail className="mr-1 size-4" /> {inv.sent_at ? 'Resend' : 'Send'}</Button>}
          {canRemind && <Button variant="secondary" onClick={onRemind} disabled={remindMut.isPending}><Send className="mr-1 size-4" /> Send reminder</Button>}
          {canRecord && <Button variant="secondary" onClick={openMarkPaid}><CheckCircle2 className="mr-1 size-4" /> Record payment</Button>}
          {canCredit && <Button variant="secondary" onClick={openCredit}><MinusCircle className="mr-1 size-4" /> Issue credit</Button>}
          {canRefund && <Button variant="secondary" onClick={openRefund}><Undo2 className="mr-1 size-4" /> Refund</Button>}
          {canWriteOff && <Button variant="danger" onClick={openWriteOff}><AlertTriangle className="mr-1 size-4" /> Write off</Button>}
          {canDeleteDraft && <Button variant="danger" onClick={() => setDeleteDraftOpen(true)}><Trash2 className="mr-1 size-4" /> Delete draft</Button>}
          {canVoid && <Button variant="danger" onClick={() => setVoidOpen(true)}><XCircle className="mr-1 size-4" /> Void</Button>}
        </Toolbar>
      }
    >
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="space-y-4 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Status</div>
                <Badge variant={STATUS_VARIANT[inv.status]} className="mt-1">{inv.status.replace(/_/g, ' ')}</Badge>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Issue / Due</div>
                <div className="text-sm">{formatDate(inv.issue_date)} → {formatDate(inv.due_date)}</div>
                {inv.po_number ? (
                  <div className="mt-1 text-xs text-muted-foreground">PO: <span className="font-medium text-foreground">{inv.po_number}</span></div>
                ) : null}
              </div>
            </div>
            <hr />
            <div>
              <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Bill to</div>
              <div className="text-sm">
                <div className="font-medium">{inv.customer_display_name}</div>
                {inv.customer_email && <div className="text-muted-foreground">{inv.customer_email}</div>}
                {[inv.billing_address_line1, inv.billing_address_line2, [inv.billing_city, inv.billing_state, inv.billing_postal_code].filter(Boolean).join(', '), inv.billing_country]
                  .filter(Boolean)
                  .map((p, i) => <div key={i} className="text-muted-foreground">{p}</div>)}
              </div>
            </div>
            <hr />
            <div>
              <div className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">Line items</div>
              <div className="divide-y rounded-md border text-sm">
                <div className="grid grid-cols-12 gap-2 bg-muted/40 px-3 py-2 text-xs uppercase text-muted-foreground">
                  <div className="col-span-6">Description</div>
                  <div className="col-span-2 text-right">Qty</div>
                  <div className="col-span-2 text-right">Unit</div>
                  <div className="col-span-2 text-right">Amount</div>
                </div>
                {inv.lines.map(l => (
                  <div key={l.id} className="grid grid-cols-12 gap-2 px-3 py-2">
                    <div className="col-span-6">
                      <div>{l.description}</div>
                      <div className="text-xs text-muted-foreground">{l.account_code}</div>
                    </div>
                    <div className="col-span-2 text-right">{Number(l.quantity)}</div>
                    <div className="col-span-2 text-right">{formatInvoiceMoney(l.unit_price, inv.currency)}</div>
                    <div className="col-span-2 text-right">{formatInvoiceMoney(l.amount, inv.currency)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-col items-end gap-1 text-sm">
                <div>Subtotal: {formatInvoiceMoney(inv.subtotal, inv.currency)}</div>
                {Number(inv.tax_total) > 0 && <div>Tax: {formatInvoiceMoney(inv.tax_total, inv.currency)}</div>}
                {Number(inv.discount) > 0 && <div>Discount: -{formatInvoiceMoney(inv.discount, inv.currency)}</div>}
                <div className="text-base font-bold">Total: {formatInvoiceMoney(inv.total, inv.currency)}</div>
                {Number(inv.amount_paid) > 0 && <div className="text-muted-foreground">Paid: {formatInvoiceMoney(inv.amount_paid, inv.currency)}</div>}
              </div>
            </div>
            {(inv.notes || inv.terms || inv.bank_details) && <>
              <hr />
              {inv.notes && <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Notes</div><div className="whitespace-pre-wrap text-sm">{inv.notes}</div></div>}
              {inv.terms && <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Terms</div><div className="whitespace-pre-wrap text-sm">{inv.terms}</div></div>}
              {inv.bank_details && <div><div className="text-xs uppercase tracking-wide text-muted-foreground">Bank details</div><div className="whitespace-pre-wrap text-sm">{inv.bank_details}</div></div>}
            </>}
          </div>
        </Card>
        <Card>
          <div className="p-4">
            <div className="mb-3 text-sm font-semibold">Activity</div>
            <ol className="text-sm" key={`activity-${inv.id}-${inv.status_history.length}`}>
              {inv.status_history.map((h, idx) => {
                // Staggered timeline: each item adds ~180ms of total runtime.
                // Dot pops first, then the connector line grows down to the
                // next dot. Arithmetic keeps every row predictable.
                const step = 180
                const dotDelay = idx * step
                const lineDelay = idx * step + 90
                const textDelay = idx * step + 40
                const isLast = idx === inv.status_history.length - 1
                return (
                  <li key={h.id} className="relative flex gap-3 pb-4 last:pb-0">
                    <div className="relative flex w-2 shrink-0 flex-col items-center">
                      <span
                        className="bk-timeline-dot mt-1.5 size-2 rounded-full bg-primary"
                        style={{ animationDelay: `${dotDelay}ms` }}
                      />
                      {!isLast && (
                        <span
                          className="bk-timeline-line absolute left-1/2 top-3.5 -ml-px h-[calc(100%-0.75rem)] w-px bg-border"
                          style={{ animationDelay: `${lineDelay}ms` }}
                        />
                      )}
                    </div>
                    <div
                      className="bk-timeline-text min-w-0 flex-1"
                      style={{ animationDelay: `${textDelay}ms` }}
                    >
                      <div className="font-medium">
                        {h.from_status ? `${h.from_status} → ${h.to_status}` : `Created as ${h.to_status}`}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDateTime(h.created_at)}{h.actor_email ? ` · ${h.actor_email}` : ''}
                      </div>
                      {h.reason && <div className="text-xs text-muted-foreground">{h.reason}</div>}
                    </div>
                  </li>
                )
              })}
            </ol>
            {inv.payments.length > 0 && (
              <>
                <hr className="my-4" />
                <div className="mb-2 text-sm font-semibold">Payments</div>
                <ul className="space-y-2 text-sm">
                  {inv.payments.map(p => (
                    <li key={p.id} className="flex justify-between">
                      <span>
                        {formatInvoiceMoney(p.amount_applied, p.currency)}
                        <span className="ml-2 text-xs text-muted-foreground">{p.method}</span>
                      </span>
                      <span className="text-xs text-muted-foreground">{formatDateTime(p.received_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Card>
      </div>

      <Modal.Confirm
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title={`Void invoice ${inv.invoice_number}?`}
        body={
          <div className="space-y-3">
            <p>This reverses the GL entries for this invoice. The invoice number stays reserved so your audit trail is preserved - nothing is deleted from the ledger.</p>
            <FormField label="Reason (required)" value={voidReason} onChange={e => setVoidReason(e.target.value)} />
          </div>
        }
        confirmLabel="Void invoice"
        confirmVariant="danger"
        busy={voidMut.isPending}
        onConfirm={onVoidConfirm}
      />

      <Modal.Confirm
        open={deleteDraftOpen}
        onOpenChange={setDeleteDraftOpen}
        title={`Delete draft ${inv.invoice_number}?`}
        body="Drafts haven't been posted to the ledger or sent to the customer, so this removes the invoice permanently. The invoice number won't be reused - numbering stays monotonic for audit."
        confirmLabel="Delete draft"
        confirmVariant="danger"
        busy={deleteDraftMut.isPending}
        onConfirm={onDeleteDraftConfirm}
      />

      <Modal.Confirm
        open={creditOpen}
        onOpenChange={setCreditOpen}
        title={`Issue credit note against ${inv.invoice_number}`}
        body={
          <div className="space-y-3">
            <p>
              Outstanding balance: <strong>{formatInvoiceMoney(outstandingNum, inv.currency)}</strong>.
              Posts Dr Sales Returns / Cr AR and shrinks this invoice&apos;s balance by the credited amount.
            </p>
            <FormField
              label={`Amount (${inv.currency})`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={creditAmount}
              onChange={e => setCreditAmount(e.target.value)}
            />
            <FormField label="Reason" value={creditReason} onChange={e => setCreditReason(e.target.value)} hint="Shown on audit log + credit-note record." />
          </div>
        }
        confirmLabel="Issue credit"
        confirmVariant="primary"
        busy={creditMut.isPending}
        onConfirm={onCreditConfirm}
      />

      <Modal.Confirm
        open={refundOpen}
        onOpenChange={setRefundOpen}
        title={`Refund payment on ${inv.invoice_number}`}
        body={
          <div className="space-y-3">
            <p>
              Pick which payment to refund. Stripe-collected payments are reversed on
              the card automatically; manual payments record a Dr AR / Cr Cash journal
              entry so reconciliation can pair the refund with the original settlement.
            </p>
            <FormField
              label="Original payment"
              control={
                <select
                  className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                  value={refundPaymentId}
                  onChange={e => {
                    setRefundPaymentId(e.target.value)
                    const sel = invoice.payments.find(p => String(p.id) === e.target.value)
                    if (sel) setRefundMethod(sel.method || 'manual')
                  }}
                >
                  <option value="">Select a payment…</option>
                  {invoice.payments.map(p => (
                    <option key={p.id} value={p.id}>
                      {formatInvoiceMoney(p.amount_applied, p.currency)} - {p.method} - {p.received_at.slice(0, 10)}
                    </option>
                  ))}
                </select>
              }
            />
            <FormField
              label={`Refund amount (${inv.currency})`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={refundAmount}
              onChange={e => setRefundAmount(e.target.value)}
            />
            <FormField label="Method" value={refundMethod} onChange={e => setRefundMethod(e.target.value)} hint="manual, ach, wire, check, stripe…" />
            <FormField label="Reason" value={refundReason} onChange={e => setRefundReason(e.target.value)} />
            <label className="flex items-start gap-2 rounded-md border p-3 text-sm">
              <Checkbox
                checked={refundPostToAccounts}
                onCheckedChange={(v) => setRefundPostToAccounts(v === true)}
                className="mt-0.5"
              />
              <span className="flex-1">
                <span className="font-medium">Post to accounting</span>
                <span className="block text-xs text-muted-foreground">
                  Default. Records a Dr AR / Cr Cash journal entry so reports reflect the refund.
                  Uncheck if you only want to reverse the card and skip the GL entry.
                </span>
              </span>
            </label>
          </div>
        }
        confirmLabel="Record refund"
        confirmVariant="primary"
        busy={refundMut.isPending}
        onConfirm={onRefundConfirm}
      />

      <Modal.Confirm
        open={writeOffOpen}
        onOpenChange={setWriteOffOpen}
        title={`Write off ${inv.invoice_number}`}
        body={
          <div className="space-y-3">
            <p>
              Moves the outstanding balance to bad-debt expense (Dr Bad Debt / Cr AR) and marks the invoice <em>written off</em>. Use this when the balance is genuinely uncollectible - if you still expect payment, use a credit note instead.
            </p>
            <FormField
              label={`Amount to write off (${inv.currency})`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={writeOffAmount}
              onChange={e => setWriteOffAmount(e.target.value)}
              hint={`Outstanding: ${formatInvoiceMoney(outstandingNum, inv.currency)}`}
            />
            <FormField label="Reason (required)" value={writeOffReason} onChange={e => setWriteOffReason(e.target.value)} />
          </div>
        }
        confirmLabel="Write off"
        confirmVariant="danger"
        busy={writeOffMut.isPending}
        onConfirm={onWriteOffConfirm}
      />

      <Modal.Confirm
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        title={`Record payment for ${inv.invoice_number}`}
        body={
          <div className="space-y-3">
            <p>
              Outstanding balance: <strong>{formatInvoiceMoney(outstandingNum, inv.currency)}</strong>.
              Enter the full balance to settle the invoice, or a smaller amount to record a partial payment (status flips to <em>partially paid</em>). Posts Dr Cash / Cr AR to the ledger for the amount recorded.
            </p>
            <FormField
              label={`Amount (${inv.currency})`}
              type="number"
              inputMode="decimal"
              step="0.01"
              min={0}
              value={markPaidAmount}
              onChange={e => setMarkPaidAmount(e.target.value)}
            />
            <FormField label="Received on" type="date" value={markPaidDate} onChange={e => setMarkPaidDate(e.target.value)} />
            <FormField label="Method" value={markPaidMethod} onChange={e => setMarkPaidMethod(e.target.value)} hint="e.g. manual, ach, wire, check" />
          </div>
        }
        confirmLabel="Record payment"
        confirmVariant="primary"
        busy={markPaidMut.isPending}
        onConfirm={onMarkPaidConfirm}
      />

    </Page>
  )
}
